/**
 * Dependency-free line diffing for dees-dataview-codebox: LCS-based
 * before/after comparison, unified-patch parsing, context folding, split
 * pairing, and intraline (word-level) change detection. Pure data — no DOM.
 */

export type TDiffRowKind = 'context' | 'add' | 'remove' | 'hunk';

export interface IDiffSegment {
  text: string;
  changed: boolean;
}

export interface IDiffRow {
  kind: TDiffRowKind;
  text: string;
  /** 1-based line number on the before side (context/remove) */
  oldLine?: number;
  /** 1-based line number on the after side (context/add) */
  newLine?: number;
  /** intraline segments for paired single-line changes */
  segments?: IDiffSegment[];
}

export interface IDiffFold {
  kind: 'fold';
  rows: IDiffRow[];
  /** stable ordinal among folds; expansion state is keyed by this */
  ordinal?: number;
}

export type TDiffDisplayRow = IDiffRow | IDiffFold;

export interface ISplitCell {
  kind: 'context' | 'add' | 'remove';
  text: string;
  line: number;
  segments?: IDiffSegment[];
}

export interface ISplitRow {
  kind: 'pair' | 'hunk';
  text?: string;
  left?: ISplitCell;
  right?: ISplitCell;
}

export interface IDiffStats {
  added: number;
  removed: number;
}

export interface IUnifiedDiffSummaryOptions {
  /** The supplied patch ends before the producer's complete output. */
  truncated?: boolean;
}

export type TUnifiedDiffSummary =
  | { type: 'exact'; stats: IDiffStats }
  | { type: 'partial'; stats?: IDiffStats }
  | { type: 'binary' }
  | { type: 'unparseable' };

export type TUnifiedDiffMetadataKind =
  | 'index'
  | 'new-file-mode'
  | 'deleted-file-mode'
  | 'old-mode'
  | 'new-mode'
  | 'similarity-index'
  | 'dissimilarity-index'
  | 'rename-from'
  | 'rename-to'
  | 'copy-from'
  | 'copy-to';

export interface IUnifiedDiffMetadata {
  kind: TUnifiedDiffMetadataKind;
  value: string;
}

/** One ordered file section from a unified patch. Paths never retain Git's a/ and b/ prefixes. */
export interface IUnifiedDiffFileSection {
  beforePath?: string;
  afterPath?: string;
  displayPath?: string;
  metadata: IUnifiedDiffMetadata[];
  rows: IDiffRow[];
  binary: boolean;
}

export type TUnifiedDiffDocumentSummary =
  | { type: 'exact'; stats?: IDiffStats; binaryFiles: number }
  | { type: 'partial'; stats?: IDiffStats; binaryFiles: number }
  | { type: 'unparseable'; binaryFiles: number };

export interface IUnifiedDiffDocument {
  files: IUnifiedDiffFileSection[];
  summary: TUnifiedDiffDocumentSummary;
}

const splitLines = (text: string): string[] => {
  if (!text) return [];
  const lines = text.split('\n');
  if (lines.length && lines[lines.length - 1] === '') lines.pop();
  return lines;
};

/** Cells beyond this budget fall back to a trimmed block-replace diff. */
const maxLcsCells = 4_000_000;

/**
 * Line-level diff of two texts. Common prefix/suffix are trimmed first so
 * the O(n*m) LCS table only covers the changed middle; oversized middles
 * degrade gracefully to one remove-block + add-block.
 */
export const computeLineDiff = (beforeText: string, afterText: string): IDiffRow[] => {
  const beforeLines = splitLines(beforeText);
  const afterLines = splitLines(afterText);

  let prefixLength = 0;
  while (
    prefixLength < beforeLines.length
    && prefixLength < afterLines.length
    && beforeLines[prefixLength] === afterLines[prefixLength]
  ) {
    prefixLength++;
  }
  let suffixLength = 0;
  while (
    suffixLength < beforeLines.length - prefixLength
    && suffixLength < afterLines.length - prefixLength
    && beforeLines[beforeLines.length - 1 - suffixLength] === afterLines[afterLines.length - 1 - suffixLength]
  ) {
    suffixLength++;
  }

  const beforeMiddle = beforeLines.slice(prefixLength, beforeLines.length - suffixLength);
  const afterMiddle = afterLines.slice(prefixLength, afterLines.length - suffixLength);

  const rows: IDiffRow[] = [];
  for (let index = 0; index < prefixLength; index++) {
    rows.push({ kind: 'context', text: beforeLines[index], oldLine: index + 1, newLine: index + 1 });
  }

  let oldLine = prefixLength + 1;
  let newLine = prefixLength + 1;

  const pushMiddle = (operations: Array<{ kind: 'context' | 'add' | 'remove'; text: string }>) => {
    for (const operation of operations) {
      if (operation.kind === 'context') {
        rows.push({ kind: 'context', text: operation.text, oldLine: oldLine++, newLine: newLine++ });
      } else if (operation.kind === 'remove') {
        rows.push({ kind: 'remove', text: operation.text, oldLine: oldLine++ });
      } else {
        rows.push({ kind: 'add', text: operation.text, newLine: newLine++ });
      }
    }
  };

  if (beforeMiddle.length * afterMiddle.length > maxLcsCells) {
    pushMiddle([
      ...beforeMiddle.map((text) => ({ kind: 'remove' as const, text })),
      ...afterMiddle.map((text) => ({ kind: 'add' as const, text })),
    ]);
  } else if (beforeMiddle.length || afterMiddle.length) {
    // classic LCS table over the trimmed middle
    const rowCount = beforeMiddle.length;
    const columnCount = afterMiddle.length;
    const table = new Uint32Array((rowCount + 1) * (columnCount + 1));
    const cell = (row: number, column: number) => table[row * (columnCount + 1) + column];
    for (let row = rowCount - 1; row >= 0; row--) {
      for (let column = columnCount - 1; column >= 0; column--) {
        table[row * (columnCount + 1) + column] = beforeMiddle[row] === afterMiddle[column]
          ? cell(row + 1, column + 1) + 1
          : Math.max(cell(row + 1, column), cell(row, column + 1));
      }
    }
    const operations: Array<{ kind: 'context' | 'add' | 'remove'; text: string }> = [];
    let row = 0;
    let column = 0;
    while (row < rowCount && column < columnCount) {
      if (beforeMiddle[row] === afterMiddle[column]) {
        operations.push({ kind: 'context', text: beforeMiddle[row] });
        row++;
        column++;
      } else if (cell(row + 1, column) >= cell(row, column + 1)) {
        operations.push({ kind: 'remove', text: beforeMiddle[row] });
        row++;
      } else {
        operations.push({ kind: 'add', text: afterMiddle[column] });
        column++;
      }
    }
    while (row < rowCount) operations.push({ kind: 'remove', text: beforeMiddle[row++] });
    while (column < columnCount) operations.push({ kind: 'add', text: afterMiddle[column++] });
    pushMiddle(operations);
  }

  for (let index = 0; index < suffixLength; index++) {
    rows.push({
      kind: 'context',
      text: beforeLines[beforeLines.length - suffixLength + index],
      oldLine: oldLine++,
      newLine: newLine++,
    });
  }

  return annotateIntraline(rows);
};

const decodeGitQuotedPath = (valueArg: string): string => {
  if (!(valueArg.startsWith('"') && valueArg.endsWith('"'))) return valueArg;
  const source = valueArg.slice(1, -1);
  const bytes: number[] = [];
  const encoder = new TextEncoder();
  const escapeBytes: Record<string, number> = {
    a: 7, b: 8, t: 9, n: 10, v: 11, f: 12, r: 13, '"': 34, '\\': 92,
  };
  for (let index = 0; index < source.length; index++) {
    const character = source[index]!;
    if (character !== '\\') {
      const codePoint = source.codePointAt(index)!;
      bytes.push(...encoder.encode(String.fromCodePoint(codePoint)));
      if (codePoint > 0xffff) index++;
      continue;
    }
    const escaped = source[++index];
    if (escaped === undefined) {
      bytes.push(92);
      break;
    }
    const octal = /^[0-7]{3}$/.exec(source.slice(index, index + 3));
    if (octal) {
      bytes.push(Number.parseInt(octal[0], 8));
      index += 2;
      continue;
    }
    bytes.push(escapeBytes[escaped] ?? escaped.charCodeAt(0));
  }
  return new TextDecoder().decode(Uint8Array.from(bytes));
};

const readGitPathToken = (
  sourceArg: string,
  startArg = 0,
): { value: string; end: number } | undefined => {
  let start = startArg;
  while (sourceArg[start] === ' ') start++;
  if (start >= sourceArg.length) return undefined;
  if (sourceArg[start] !== '"') return { value: sourceArg.slice(start), end: sourceArg.length };
  let escaped = false;
  for (let index = start + 1; index < sourceArg.length; index++) {
    const character = sourceArg[index]!;
    if (character === '"' && !escaped) {
      return {
        value: decodeGitQuotedPath(sourceArg.slice(start, index + 1)),
        end: index + 1,
      };
    }
    escaped = character === '\\' && !escaped;
    if (character !== '\\') escaped = false;
  }
  return undefined;
};

const normalizeGitPath = (valueArg: string | undefined): string | undefined => {
  if (!valueArg) return undefined;
  const value = decodeGitQuotedPath(valueArg);
  if (value === '/dev/null') return undefined;
  return value.startsWith('a/') || value.startsWith('b/') ? value.slice(2) : value;
};

const parsePatchHeaderPath = (valueArg: string): string | undefined => {
  const value = valueArg.startsWith('"')
    ? readGitPathToken(valueArg)?.value
    : valueArg.split('\t', 1)[0];
  return normalizeGitPath(value);
};

const parseMetadataPath = (valueArg: string): string | undefined => {
  const value = valueArg.startsWith('"')
    ? readGitPathToken(valueArg)?.value
    : valueArg;
  return value === '/dev/null' ? undefined : value;
};

const parseDiffGitPaths = (lineArg: string): { beforePath?: string; afterPath?: string } => {
  const value = lineArg.slice('diff --git '.length);
  if (value.startsWith('"')) {
    const before = readGitPathToken(value);
    const after = before ? readGitPathToken(value, before.end) : undefined;
    const beforePath = normalizeGitPath(before?.value);
    const afterPath = normalizeGitPath(after?.value);
    return {
      ...(beforePath ? { beforePath } : {}),
      ...(afterPath ? { afterPath } : {}),
    };
  }
  const boundary = Math.max(value.lastIndexOf(' b/'), value.lastIndexOf(' "b/'));
  if (boundary < 0) return {};
  const beforePath = normalizeGitPath(value.slice(0, boundary));
  const afterToken = value.slice(boundary + 1);
  const afterPath = normalizeGitPath(afterToken.startsWith('"')
    ? readGitPathToken(afterToken)?.value
    : afterToken);
  return {
    ...(beforePath ? { beforePath } : {}),
    ...(afterPath ? { afterPath } : {}),
  };
};

const metadataLine = (lineArg: string): IUnifiedDiffMetadata | undefined => {
  const kinds: Array<[TUnifiedDiffMetadataKind, string]> = [
    ['new-file-mode', 'new file mode '],
    ['deleted-file-mode', 'deleted file mode '],
    ['old-mode', 'old mode '],
    ['new-mode', 'new mode '],
    ['similarity-index', 'similarity index '],
    ['dissimilarity-index', 'dissimilarity index '],
    ['rename-from', 'rename from '],
    ['rename-to', 'rename to '],
    ['copy-from', 'copy from '],
    ['copy-to', 'copy to '],
    ['index', 'index '],
  ];
  const match = kinds.find(([, prefix]) => lineArg.startsWith(prefix));
  if (!match) return undefined;
  const rawValue = lineArg.slice(match[1].length);
  const pathMetadata = match[0] === 'rename-from'
    || match[0] === 'rename-to'
    || match[0] === 'copy-from'
    || match[0] === 'copy-to';
  return {
    kind: match[0],
    value: pathMetadata ? parseMetadataPath(rawValue) ?? rawValue : rawValue,
  };
};

/**
 * Parses one unified patch without discarding file boundaries or extended Git metadata.
 * Only complete hunks contribute to summary counts. A producer-declared truncation therefore
 * reports a trustworthy complete prefix instead of inventing totals for the missing suffix.
 */
export const parseUnifiedDiffDocument = (
  patchText: string,
  options: IUnifiedDiffSummaryOptions = {},
): IUnifiedDiffDocument => {
  const files: IUnifiedDiffFileSection[] = [];
  let current: IUnifiedDiffFileSection | undefined;
  let currentSawOldHeader = false;
  let currentSawNewHeader = false;
  let sawRecognizedContent = false;
  let sawHeaderPair = false;
  let unparseable = false;
  let binaryPayload = false;
  let added = 0;
  let removed = 0;
  let completeHunks = 0;
  let hunk: {
    oldLine: number;
    newLine: number;
    expectedOld: number;
    expectedNew: number;
    observedOld: number;
    observedNew: number;
    added: number;
    removed: number;
  } | undefined;

  const createFile = (pathsArg: { beforePath?: string; afterPath?: string } = {}) => {
    current = {
      ...pathsArg,
      displayPath: pathsArg.afterPath ?? pathsArg.beforePath,
      metadata: [],
      rows: [],
      binary: false,
    };
    files.push(current);
    currentSawOldHeader = false;
    currentSawNewHeader = false;
    binaryPayload = false;
    return current;
  };
  const requireFile = () => current ?? createFile();
  const refreshDisplayPath = (fileArg: IUnifiedDiffFileSection) => {
    fileArg.displayPath = fileArg.afterPath ?? fileArg.beforePath;
  };
  const completeHunk = () => {
    if (!hunk
      || hunk.observedOld !== hunk.expectedOld
      || hunk.observedNew !== hunk.expectedNew) return false;
    added += hunk.added;
    removed += hunk.removed;
    completeHunks++;
    hunk = undefined;
    return true;
  };

  for (const line of splitLines(patchText)) {
    if (line.startsWith('diff --git ')) {
      if (hunk) {
        unparseable = true;
        hunk = undefined;
      }
      createFile(parseDiffGitPaths(line));
      continue;
    }

    const hunkMatch = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@(.*)$/.exec(line);
    if (hunkMatch) {
      if (hunk) unparseable = true;
      const file = requireFile();
      hunk = {
        oldLine: Number(hunkMatch[1]),
        newLine: Number(hunkMatch[3]),
        expectedOld: hunkMatch[2] === undefined ? 1 : Number(hunkMatch[2]),
        expectedNew: hunkMatch[4] === undefined ? 1 : Number(hunkMatch[4]),
        observedOld: 0,
        observedNew: 0,
        added: 0,
        removed: 0,
      };
      file.rows.push({ kind: 'hunk', text: line });
      sawRecognizedContent = true;
      completeHunk();
      continue;
    }

    if (line.startsWith('\\ No newline at end of file')) continue;
    if (hunk) {
      const file = requireFile();
      if (line.startsWith('+')) {
        file.rows.push({ kind: 'add', text: line.slice(1), newLine: hunk.newLine++ });
        hunk.observedNew++;
        hunk.added++;
      } else if (line.startsWith('-')) {
        file.rows.push({ kind: 'remove', text: line.slice(1), oldLine: hunk.oldLine++ });
        hunk.observedOld++;
        hunk.removed++;
      } else if (line.startsWith(' ')) {
        file.rows.push({
          kind: 'context',
          text: line.slice(1),
          oldLine: hunk.oldLine++,
          newLine: hunk.newLine++,
        });
        hunk.observedOld++;
        hunk.observedNew++;
      } else {
        unparseable = true;
        hunk = undefined;
        continue;
      }
      if (hunk
        && (hunk.observedOld > hunk.expectedOld || hunk.observedNew > hunk.expectedNew)) {
        unparseable = true;
        hunk = undefined;
      } else {
        completeHunk();
      }
      continue;
    }

    if (binaryPayload) continue;
    if (line.startsWith('--- ')) {
      if (currentSawNewHeader) createFile();
      const file = requireFile();
      file.beforePath = parsePatchHeaderPath(line.slice(4));
      currentSawOldHeader = true;
      refreshDisplayPath(file);
      continue;
    }
    if (line.startsWith('+++ ')) {
      const file = requireFile();
      file.afterPath = parsePatchHeaderPath(line.slice(4));
      currentSawNewHeader = true;
      sawHeaderPair ||= currentSawOldHeader;
      refreshDisplayPath(file);
      continue;
    }
    const metadata = metadataLine(line);
    if (metadata) {
      const file = requireFile();
      file.metadata.push(metadata);
      if (metadata.kind === 'rename-from' || metadata.kind === 'copy-from') {
        file.beforePath ??= metadata.value;
      }
      if (metadata.kind === 'rename-to' || metadata.kind === 'copy-to') {
        file.afterPath ??= metadata.value;
      }
      refreshDisplayPath(file);
      sawRecognizedContent = true;
      continue;
    }
    const binaryMatch = /^Binary files (.+) and (.+) differ$/.exec(line);
    if (binaryMatch) {
      const file = requireFile();
      file.beforePath ??= parsePatchHeaderPath(binaryMatch[1]!);
      file.afterPath ??= parsePatchHeaderPath(binaryMatch[2]!);
      refreshDisplayPath(file);
      file.binary = true;
      sawRecognizedContent = true;
      continue;
    }
    if (line === 'GIT binary patch') {
      requireFile().binary = true;
      binaryPayload = true;
      sawRecognizedContent = true;
      continue;
    }
    if (line.trim()) unparseable = true;
  }

  if (hunk) {
    if (!options.truncated) unparseable = true;
    hunk = undefined;
  }
  for (const file of files) file.rows = annotateIntraline(file.rows);
  const binaryFiles = files.filter((file) => file.binary).length;
  const stats = completeHunks > 0 ? { added, removed } : undefined;
  const recognized = sawRecognizedContent || sawHeaderPair || binaryFiles > 0;
  const summary: TUnifiedDiffDocumentSummary = unparseable || !recognized
    ? { type: 'unparseable', binaryFiles }
    : options.truncated
      ? { type: 'partial', ...(stats ? { stats } : {}), binaryFiles }
      : { type: 'exact', ...(stats ? { stats } : {}), binaryFiles };
  return { files, summary };
};

/** Compatibility row view. File boundaries and metadata are available from parseUnifiedDiffDocument. */
export const parseUnifiedDiff = (patchText: string): IDiffRow[] =>
  parseUnifiedDiffDocument(patchText).files.flatMap((file) => file.rows);

/**
 * Returns trustworthy change counts for a unified patch. Declared hunk sizes
 * are validated before their rows contribute to the result. A truncated patch
 * reports only the complete hunk prefix, and omits stats when none is complete.
 */
export const summarizeUnifiedDiff = (
  patchText: string,
  options: IUnifiedDiffSummaryOptions = {},
): TUnifiedDiffSummary => {
  const document = parseUnifiedDiffDocument(patchText, options);
  if (document.summary.binaryFiles > 0) return { type: 'binary' };
  if (document.summary.type === 'unparseable') return { type: 'unparseable' };
  if (document.summary.type === 'partial') {
    return document.summary.stats
      ? { type: 'partial', stats: document.summary.stats }
      : { type: 'partial' };
  }
  return { type: 'exact', stats: document.summary.stats ?? { added: 0, removed: 0 } };
};

const maxIntralineChars = 400;

/**
 * Word-level segments for balanced remove/add runs (the GitHub-style
 * intraline tint). Only 1:1 paired lines within a run are annotated.
 */
const annotateIntraline = (rows: IDiffRow[]): IDiffRow[] => {
  for (let index = 0; index < rows.length; index++) {
    if (rows[index].kind !== 'remove') continue;
    const removeStart = index;
    let removeEnd = index;
    while (removeEnd + 1 < rows.length && rows[removeEnd + 1].kind === 'remove') removeEnd++;
    let addEnd = removeEnd;
    while (addEnd + 1 < rows.length && rows[addEnd + 1].kind === 'add') addEnd++;
    const removeCount = removeEnd - removeStart + 1;
    const addCount = addEnd - removeEnd;
    if (addCount === removeCount) {
      for (let pairIndex = 0; pairIndex < removeCount; pairIndex++) {
        const removeRow = rows[removeStart + pairIndex];
        const addRow = rows[removeEnd + 1 + pairIndex];
        if (removeRow.text.length <= maxIntralineChars && addRow.text.length <= maxIntralineChars) {
          const [removeSegments, addSegments] = intralineSegments(removeRow.text, addRow.text);
          removeRow.segments = removeSegments;
          addRow.segments = addSegments;
        }
      }
    }
    index = addEnd;
  }
  return rows;
};

const tokenizeForIntraline = (text: string): string[] => {
  return text.match(/(\s+|[A-Za-z0-9_]+|[^\sA-Za-z0-9_]+)/g) ?? [];
};

const intralineSegments = (beforeText: string, afterText: string): [IDiffSegment[], IDiffSegment[]] => {
  const beforeTokens = tokenizeForIntraline(beforeText);
  const afterTokens = tokenizeForIntraline(afterText);
  let prefixLength = 0;
  while (
    prefixLength < beforeTokens.length
    && prefixLength < afterTokens.length
    && beforeTokens[prefixLength] === afterTokens[prefixLength]
  ) {
    prefixLength++;
  }
  let suffixLength = 0;
  while (
    suffixLength < beforeTokens.length - prefixLength
    && suffixLength < afterTokens.length - prefixLength
    && beforeTokens[beforeTokens.length - 1 - suffixLength] === afterTokens[afterTokens.length - 1 - suffixLength]
  ) {
    suffixLength++;
  }
  const buildSegments = (tokens: string[]): IDiffSegment[] => {
    const prefix = tokens.slice(0, prefixLength).join('');
    const middle = tokens.slice(prefixLength, tokens.length - suffixLength).join('');
    const suffix = tokens.slice(tokens.length - suffixLength).join('');
    const segments: IDiffSegment[] = [];
    if (prefix) segments.push({ text: prefix, changed: false });
    if (middle) segments.push({ text: middle, changed: true });
    if (suffix) segments.push({ text: suffix, changed: false });
    return segments.length ? segments : [{ text: tokens.join(''), changed: false }];
  };
  return [buildSegments(beforeTokens), buildSegments(afterTokens)];
};

/**
 * Folds long unchanged context runs so large files read as a review diff:
 * keeps `contextLines` around changes, folds runs longer than
 * `contextLines * 2 + minFoldSize`.
 */
export const foldContextRows = (
  rows: IDiffRow[],
  options: { contextLines?: number; minFoldSize?: number } = {},
): TDiffDisplayRow[] => {
  const contextLines = options.contextLines ?? 3;
  const minFoldSize = options.minFoldSize ?? 4;
  const displayRows: TDiffDisplayRow[] = [];
  let contextRun: IDiffRow[] = [];
  let seenChange = false;

  const flushRun = (isEnd: boolean) => {
    if (!contextRun.length) return;
    const keepHead = seenChange ? contextLines : 0;
    const keepTail = isEnd ? 0 : contextLines;
    if (contextRun.length > keepHead + keepTail + minFoldSize) {
      displayRows.push(...contextRun.slice(0, keepHead));
      displayRows.push({ kind: 'fold', rows: contextRun.slice(keepHead, contextRun.length - keepTail) });
      if (keepTail) displayRows.push(...contextRun.slice(contextRun.length - keepTail));
    } else {
      displayRows.push(...contextRun);
    }
    contextRun = [];
  };

  for (const row of rows) {
    if (row.kind === 'context') {
      contextRun.push(row);
    } else {
      flushRun(false);
      seenChange = true;
      displayRows.push(row);
    }
  }
  flushRun(true);
  return displayRows;
};

/** Pairs rows into side-by-side cells; balanced remove/add runs align 1:1. */
export const buildSplitRows = (rows: Array<IDiffRow>): ISplitRow[] => {
  const splitRows: ISplitRow[] = [];
  let index = 0;
  while (index < rows.length) {
    const row = rows[index];
    if (row.kind === 'hunk') {
      splitRows.push({ kind: 'hunk', text: row.text });
      index++;
      continue;
    }
    if (row.kind === 'context') {
      splitRows.push({
        kind: 'pair',
        left: { kind: 'context', text: row.text, line: row.oldLine ?? 0 },
        right: { kind: 'context', text: row.text, line: row.newLine ?? 0 },
      });
      index++;
      continue;
    }
    // change block: collect consecutive removes then adds, pair them up
    const removes: IDiffRow[] = [];
    const adds: IDiffRow[] = [];
    while (index < rows.length && rows[index].kind === 'remove') removes.push(rows[index++]);
    while (index < rows.length && rows[index].kind === 'add') adds.push(rows[index++]);
    const pairCount = Math.max(removes.length, adds.length);
    for (let pairIndex = 0; pairIndex < pairCount; pairIndex++) {
      const removeRow = removes[pairIndex];
      const addRow = adds[pairIndex];
      splitRows.push({
        kind: 'pair',
        left: removeRow
          ? { kind: 'remove', text: removeRow.text, line: removeRow.oldLine ?? 0, segments: removeRow.segments }
          : undefined,
        right: addRow
          ? { kind: 'add', text: addRow.text, line: addRow.newLine ?? 0, segments: addRow.segments }
          : undefined,
      });
    }
  }
  return splitRows;
};

export const diffStats = (rows: Array<IDiffRow>): IDiffStats => {
  let added = 0;
  let removed = 0;
  for (const row of rows) {
    if (row.kind === 'add') added++;
    else if (row.kind === 'remove') removed++;
  }
  return { added, removed };
};

/** Serializes rows back into a unified diff (for copy-to-clipboard). */
export const toUnifiedDiff = (
  rows: IDiffRow[],
  options: { contextLines?: number; fileNameBefore?: string; fileNameAfter?: string } = {},
): string => {
  const contextLines = options.contextLines ?? 3;
  const lines: string[] = [];
  if (options.fileNameBefore || options.fileNameAfter) {
    lines.push(`--- ${options.fileNameBefore ?? 'a'}`);
    lines.push(`+++ ${options.fileNameAfter ?? 'b'}`);
  }

  interface IHunk { startIndex: number; endIndex: number; }
  const hunks: IHunk[] = [];
  let currentHunk: IHunk | undefined;
  rows.forEach((row, index) => {
    if (row.kind === 'add' || row.kind === 'remove') {
      const startIndex = Math.max(0, index - contextLines);
      if (currentHunk && startIndex <= currentHunk.endIndex + 1) {
        currentHunk.endIndex = Math.min(rows.length - 1, index + contextLines);
      } else {
        currentHunk = { startIndex, endIndex: Math.min(rows.length - 1, index + contextLines) };
        hunks.push(currentHunk);
      }
    }
  });

  for (const hunk of hunks) {
    const hunkRows = rows
      .slice(hunk.startIndex, hunk.endIndex + 1)
      .filter((row) => row.kind !== 'hunk');
    const firstOld = hunkRows.find((row) => row.oldLine !== undefined)?.oldLine ?? 0;
    const firstNew = hunkRows.find((row) => row.newLine !== undefined)?.newLine ?? 0;
    const oldCount = hunkRows.filter((row) => row.kind === 'context' || row.kind === 'remove').length;
    const newCount = hunkRows.filter((row) => row.kind === 'context' || row.kind === 'add').length;
    lines.push(`@@ -${firstOld},${oldCount} +${firstNew},${newCount} @@`);
    for (const row of hunkRows) {
      lines.push(`${row.kind === 'add' ? '+' : row.kind === 'remove' ? '-' : ' '}${row.text}`);
    }
  }
  return lines.join('\n');
};
